import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, TextField, Button,
  Grid, Alert, MenuItem, IconButton, Chip,
} from '@mui/material';
import api from '../../services/api.js';
import GradeFormDialog from '../../components/GradeFormDialog.jsx';
import { TableKit } from '@NP-Webber/crm-kit';
import { formatHebrewDate } from '../../utils/hebrewDate';
import AdminPageHeader from '../../next/AdminPageHeader.jsx';

const EMPTY_RANGE = { simanFrom: '', simanTo: '' };
const EMPTY_LINK  = { chelek: '', ranges: [{ ...EMPTY_RANGE }] };

const EMPTY_FORM = {
  code:      '',
  name:      '',
  type:      'time',
  subject:   '',
  date:      '',
  status:    'draft',
  kolelCode: '',
  city:      '',
  periodId:  '',
  graderNotes: '',
  introText: '',
  libraryLinks: [],
};

const normalizePeriodId = (periodId) => {
  if (!periodId) return '';
  if (typeof periodId === 'object') return periodId._id || '';
  return periodId;
};

const TYPE_OPTIONS   = [{ value: 'time', label: 'זמן' }, { value: 'shimur', label: 'שימור' }, { value: 'miktzoa', label: 'מקצוע' }];
const STATUS_OPTIONS = [{ value: 'draft', label: 'טיוטה' }, { value: 'open', label: 'פתוח' }, { value: 'closed', label: 'סגור' }, { value: 'graded', label: 'צויין' }];
const CHELEK_OPTIONS = [
  { value: '', label: '—' },
  { value: 'אורח חיים', label: 'אורח חיים' },
  { value: 'יורה דעה', label: 'יורה דעה' },
  { value: 'אבן העזר', label: 'אבן העזר' },
  { value: 'חושן משפט', label: 'חושן משפט' },
];

const ExamCard = ({ recordId, onClose }) => {
  const params   = useParams();
  const navigate = useNavigate();
  const id  = recordId || params.id;
  const isNew = !id || id === 'new';

  const [form, setForm]       = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [periods, setPeriods] = useState([]);

  // ציונים למבחן
  const [grades, setGrades]             = useState([]);
  const [gradesLoading, setGradesLoading] = useState(false);
  const [gradeDialogOpen, setGradeDialogOpen] = useState(false);
  const [editingGrade, setEditingGrade] = useState(null);

  useEffect(() => {
    api.get('/exam-periods').then(({ data }) => {
      const list = data.data || [];
      setPeriods(list);
      if (isNew) {
        const active = list.find((p) => p.isActive);
        if (active) {
          setForm((f) => (f.periodId ? f : { ...f, periodId: active._id }));
        }
      }
    }).catch(() => {});
  }, [isNew]);

  const fetchGrades = useCallback(async () => {
    if (isNew || !id) return;
    setGradesLoading(true);
    try {
      const { data } = await api.get('/grades', { params: { examId: id, all: 1 } });
      setGrades(data.data);
    } catch (e) {
      console.error('שגיאה בטעינת ציונים:', e);
    } finally {
      setGradesLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => { fetchGrades(); }, [fetchGrades]);

  useEffect(() => {
    if (!isNew && id) {
      setLoading(true);
      api.get(`/exams/${id}`)
        .then(({ data }) => {
          // המרת תאריך לפורמט YYYY-MM-DD לשדה input[type=date]
          const dateStr = data.date ? new Date(data.date).toISOString().slice(0, 10) : '';
          setForm({
            ...data,
            date: dateStr,
            periodId: normalizePeriodId(data.periodId),
          });
        })
        .catch(() => setError('שגיאה בטעינת נתוני מבחן'))
        .finally(() => setLoading(false));
    }
  }, [id, isNew]);

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
  };

  // ── Library links helpers ──
  const updateLinks = (fn) => setForm((f) => ({ ...f, libraryLinks: fn(f.libraryLinks || []) }));

  const addLink = () => updateLinks((links) => [...links, { ...EMPTY_LINK, ranges: [{ ...EMPTY_RANGE }] }]);
  const removeLink = (li) => updateLinks((links) => links.filter((_, i) => i !== li));
  const changeLinkChelek = (li, val) => updateLinks((links) =>
    links.map((l, i) => i === li ? { ...l, chelek: val } : l)
  );
  const addRange = (li) => updateLinks((links) =>
    links.map((l, i) => i === li ? { ...l, ranges: [...(l.ranges || []), { ...EMPTY_RANGE }] } : l)
  );
  const removeRange = (li, ri) => updateLinks((links) =>
    links.map((l, i) => i === li ? { ...l, ranges: l.ranges.filter((_, j) => j !== ri) } : l)
  );
  const changeRange = (li, ri, field, val) => updateLinks((links) =>
    links.map((l, i) => i === li
      ? { ...l, ranges: l.ranges.map((r, j) => j === ri ? { ...r, [field]: val } : r) }
      : l
    )
  );

  const handleSave = async () => {
    setError('');
    setSuccess('');
    try {
      const payload = {
        ...form,
        code: form.code ? Number(form.code) : form.code,
        kolelCode: form.kolelCode ? Number(form.kolelCode) : null,
        periodId: form.periodId || null,
      };
      if (isNew) {
        const { data } = await api.post('/exams', payload);
        setSuccess('מבחן נוצר בהצלחה');
        if (onClose) onClose('created');
        else navigate(`/exams/${data._id}`);
      } else {
        const { data } = await api.put(`/exams/${id}`, payload);
        const dateStr = data.date ? new Date(data.date).toISOString().slice(0, 10) : '';
        setForm({
          ...data,
          date: dateStr,
          periodId: normalizePeriodId(data.periodId),
        });
        setSuccess('נשמר בהצלחה');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאת שמירה');
    }
  };

  if (loading) return <Box><Typography>טוען...</Typography></Box>;

  return (
    <Box>
      <AdminPageHeader
        title={isNew ? 'מבחן חדש' : form.name || 'מבחן'}
        sub={isNew ? 'כרטיס מבחן' : [form.code ? `קוד ${form.code}` : null, form.subject].filter(Boolean).join(' · ') || 'כרטיס מבחן'}
        back={!recordId ? { label: 'מבחנים', onClick: () => navigate('/exams') } : undefined}
        legacy={(
          <Typography variant="h5" fontWeight={700} gutterBottom>
            {isNew ? 'מבחן חדש' : form.name || 'מבחן'}
          </Typography>
        )}
      />

      {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>פרטי מבחן</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <TextField
              label="קוד"
              fullWidth size="small"
              type="number"
              inputProps={{ min: 1, step: 1 }}
              value={form.code || ''}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.replace(/\D/g, '') }))}
              required
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              label="שם מבחן"
              fullWidth size="small"
              value={form.name || ''}
              onChange={handleChange('name')}
              required
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              select label="סוג"
              fullWidth size="small"
              value={form.type || 'time'}
              onChange={handleChange('type')}
              required
            >
              {TYPE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="נושא"
              fullWidth size="small"
              value={form.subject || ''}
              onChange={handleChange('subject')}
              required
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              label="תאריך"
              type="date"
              fullWidth size="small"
              value={form.date || ''}
              onChange={handleChange('date')}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              select label="סטטוס"
              fullWidth size="small"
              value={form.status || 'draft'}
              onChange={handleChange('status')}
            >
              {STATUS_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              select
              label="תקופה"
              fullWidth
              size="small"
              value={form.periodId || ''}
              onChange={handleChange('periodId')}
            >
              <MenuItem value="">ללא תקופה</MenuItem>
              {periods.map((p) => (
                <MenuItem key={p._id} value={p._id}>
                  {p.name}{p.isActive ? ' (פעיל)' : ''}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              label="קוד כולל"
              fullWidth size="small"
              value={form.kolelCode || ''}
              onChange={handleChange('kolelCode')}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              label="עיר"
              fullWidth size="small"
              value={form.city || ''}
              InputProps={{ readOnly: true }}
              helperText="משויכת אוטומטית לפי קוד כולל"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="טקסט בתחילת המבחן"
              fullWidth
              size="small"
              multiline
              rows={3}
              value={form.introText || ''}
              onChange={handleChange('introText')}
              helperText="לדוג׳: מי שיש לו שני מבחנים עונה רק על שאלות עם כוכבית"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="הערות והארות (לבודק)"
              fullWidth
              size="small"
              multiline
              rows={3}
              value={form.graderNotes || ''}
              onChange={handleChange('graderNotes')}
              helperText="מופיע בסוף דף הבחינה"
            />
          </Grid>
        </Grid>

        {/* שיוך לספריית שאלות */}
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 3, mb: 1, gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>שיוך לספריית שאלות</Typography>
          <Button size="small" variant="outlined" onClick={addLink}>+ הוסף חלק</Button>
        </Box>
        {(form.libraryLinks || []).length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            לא הוגדרו שיוכים לספרייה. לחץ "הוסף חלק" כדי לשייך סימנים.
          </Typography>
        )}
        {(form.libraryLinks || []).map((link, li) => (
          <Box key={li} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 1.5 }}>
            {/* שורת החלק */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <TextField
                select label="חלק"
                size="small"
                sx={{ minWidth: 160 }}
                value={link.chelek || ''}
                onChange={(e) => changeLinkChelek(li, e.target.value)}
              >
                {CHELEK_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
              <Button size="small" variant="text" onClick={() => addRange(li)} disabled={!link.chelek}>
                + רצף
              </Button>
              <IconButton size="small" color="error" onClick={() => removeLink(li)} title="הסר חלק">✕</IconButton>
            </Box>
            {/* רצפי הסימנים */}
            {(link.ranges || []).map((range, ri) => (
              <Box key={ri} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, pr: 1 }}>
                <TextField
                  label="מסימן"
                  size="small"
                  type="number"
                  inputProps={{ min: 1, step: 1 }}
                  sx={{ width: 110 }}
                  value={range.simanFrom || ''}
                  onChange={(e) => changeRange(li, ri, 'simanFrom', e.target.value.replace(/\D/g, ''))}
                  disabled={!link.chelek}
                />
                <Typography variant="body2" color="text.secondary">—</Typography>
                <TextField
                  label="עד סימן"
                  size="small"
                  type="number"
                  inputProps={{ min: 1, step: 1 }}
                  sx={{ width: 110 }}
                  value={range.simanTo || ''}
                  onChange={(e) => changeRange(li, ri, 'simanTo', e.target.value.replace(/\D/g, ''))}
                  disabled={!link.chelek}
                />
                {(link.ranges || []).length > 1 && (
                  <IconButton size="small" onClick={() => removeRange(li, ri)} title="הסר רצף">✕</IconButton>
                )}
              </Box>
            ))}
          </Box>
        ))}
      </Paper>

      {/* ── ציונים ── */}
      {!isNew && (
        <Paper sx={{ p: 3, mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              ציונים ({grades.length})
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={() => { setEditingGrade(null); setGradeDialogOpen(true); }}
            >
              + הוסף ציון
            </Button>
          </Box>
          <TableKit
            columns={[
              { key: 'memberId', title: 'שם', sortable: false,
                render: (v) => v ? `${v.firstName || ''} ${v.lastName || ''}`.trim() : '—' },
              { key: 'memberId', title: 'ת׳ז', sortable: false,
                render: (v) => v?.idNumber || '—' },
              { key: 'tractate', title: 'מסכת', sortable: false, render: (v) => v || '—' },
              { key: 'score', title: 'ציון', sortable: true,
                render: (v) => v >= 90 ? <Chip label={v} color="success" size="small" /> : v },
              { key: 'source', title: 'מקור', sortable: false,
                render: (v) => v === 'ivr' ? '📞' : '🌐' },
              { key: 'enteredAt', title: 'תאריך', sortable: true,
                render: (v) => formatHebrewDate(v) },
              { key: '_actions', title: '', sortable: false,
                render: (_, row) => (
                  <Button size="small" onClick={() => { setEditingGrade(row); setGradeDialogOpen(true); }}>
                    ערוך
                  </Button>
                ),
              },
            ]}
            data={grades}
            total={grades.length}
            loading={gradesLoading}
            clientSideMode
            emptyMessage="לא נושאו ציונים למבחן זה"
            showFilters={false}
            exportFileName={`ציונים_${form.code || ''}`}
          />
        </Paper>
      )}

      <GradeFormDialog
        open={gradeDialogOpen}
        onClose={() => { setGradeDialogOpen(false); setEditingGrade(null); }}
        exam={!isNew ? { _id: id, name: form.name, code: form.code, type: form.type, subject: form.subject } : null}
        grade={editingGrade}
        onSaved={() => fetchGrades()}
      />

      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
        <Button variant="contained" onClick={handleSave}>שמור</Button>
        <Button variant="outlined" onClick={() => onClose ? onClose() : navigate('/exams')}>ביטול</Button>
      </Box>
    </Box>
  );
};

export default ExamCard;
